import React, { useState } from "react";
import { useStripe, useElements, CardElement } from "@stripe/react-stripe-js";
import Toast from "../../base/components/Toast";
import AuthService from "../../base/services/authentication.service";
import { SessionService } from "../../base/services/session.service";
import { isRecaptchaUnavailable, useRecaptchaHeaders } from "../../hooks/useRecaptchaHeaders";
import { fundsService } from "./fundsService";

const CARD_OPTIONS = { hidePostalCode: true };

/**
 * Collects a card via Stripe's CardElement and attaches it to the organization.
 * It does NOT charge — the purchase happens afterwards on the saved card. On
 * success the parent re-fetches the saved card (`onCardSaved`) and advances to
 * the buy step; `onClose` closes the surrounding modal (used by the edit flow).
 *
 * `onProcessing` mirrors the in-flight state up so the parent can block the
 * modal from closing while the attach (and the refresh that follows) runs. It
 * stays true on the success path — the parent's `onCardSaved` re-fetch owns
 * clearing it once that completes.
 *
 * This is the funds feature's own copy, kept separate from the shared
 * pages/auth/payment/StripeForm so changes here can't affect its other uses.
 */
const AddCardForm = ({
   onCardSaved = () => {},
   onClose = () => {},
   onProcessing = () => {},
   processing = false
}) => {
   const stripe = useStripe();
   const elements = useElements();
   const recaptchaHeaders = useRecaptchaHeaders();
   const [isLoading, setIsLoading] = useState(false);
   const organizationId = AuthService.getOrganizationId();

   const handleSubmit = async (e) => {
      e.preventDefault();
      if (!stripe || !elements) return; // Stripe.js hasn't loaded yet.

      setIsLoading(true);
      onProcessing(true);

      try {
         const { paymentMethod } = await stripe.createPaymentMethod({
            type: "card",
            card: elements.getElement(CardElement)
         });

         if (!paymentMethod?.id) {
            Toast.warning("Could not read the card details. Please try again.");
            setIsLoading(false);
            onProcessing(false);
            return;
         }

         fundsService.addPaymentMethod(
            {
               paymentMethodId: paymentMethod.id,
               organizationId: organizationId ?? SessionService.getItem("currentOrgId"),
               headers: await recaptchaHeaders()
            },
            (response) => {
               if (!response) {
                  setIsLoading(false);
                  onProcessing(false);
                  return;
               }
               // Keep the modal open until onCardSaved's re-fetch of the saved
               // card (getStripeSubscriptionByOrganization) resolves, so it
               // can't close before the new card is reflected. onProcessing is
               // left true: that re-fetch owns clearing it once it completes.
               Promise.resolve(onCardSaved())
                  .catch(() => {})
                  .finally(() => {
                     setIsLoading(false);
                     onClose();
                  });
            },
            (error) => {
               Toast.warning(error?.response?.data?.message ?? "Could not save the card.");
               setIsLoading(false);
               onProcessing(false);
            }
         );
      } catch (error) {
         // A card Stripe couldn't read, or a security check that didn't load
         // (and so a card that was never sent), is said as it is.
         const isCardError =
            error.type === "card_error" || error.type === "validation_error";
         Toast.warning(
            isCardError || isRecaptchaUnavailable(error)
               ? error.message
               : "An unexpected error occurred."
         );
         setIsLoading(false);
         onProcessing(false);
      }
   };

   const isBusy = isLoading || processing;

   return (
      <div className="stripe-form">
         <form id="payment-form" style={{ width: "100%" }} onSubmit={handleSubmit}>
            <CardElement id="payment-element" options={CARD_OPTIONS} />
            <button disabled={isBusy || !stripe || !elements} id="submit">
               <span id="button-text">
                  {isBusy ? <div className="spinner" id="spinner" /> : "Add payment method"}
               </span>
            </button>
         </form>
      </div>
   );
};

export default AddCardForm;
